import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";

/**
 * Workshop: Right detail panel — 240px wide, full-height scrollable panel.
 */

type RightPanelTab = "details" | "logs" | "actions";

interface DetailRow {
  label: string;
  value: string;
}

interface DetailSection {
  title: string;
  rows: DetailRow[];
}

interface RightPanelProps {
  title?: string;
  activeTab?: RightPanelTab;
  sections?: DetailSection[];
  logsContent?: React.ReactNode;
  actionsContent?: React.ReactNode;
  onTabChange?: (tab: RightPanelTab) => void;
  onClose?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  className?: string;
  "data-testid"?: string;
}

function RightPanelTabButton({
  label,
  isActive,
  onClick,
  testId,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      className={cn(
        "flex-1 rounded-lg py-1.5 text-center text-[11px] font-medium transition-colors",
        isActive
          ? "bg-glass-fill-heavy text-primary"
          : "text-muted hover:bg-glass-stroke-subtle hover:text-secondary",
      )}
      onClick={onClick}
      data-testid={testId}
      aria-selected={isActive}
      role="tab"
    >
      {label}
    </button>
  );
}

function RightPanelSection({
  section,
  testId,
}: {
  section: DetailSection;
  testId?: string;
}) {
  return (
    <div className="flex flex-col border-b border-glass-stroke-subtle" data-testid={testId}>
      <div className="px-3.5 pt-3">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-secondary">
          {section.title}
        </span>
      </div>
      <div className="flex flex-col gap-2 px-3.5 pb-3">
        {section.rows.map((row, index) => (
          <div key={index} className="flex items-baseline justify-between gap-2">
            <span className="shrink-0 text-[11px] text-muted">{row.label}</span>
            <span className="truncate text-right text-[11px] font-medium text-secondary">
              {row.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function RightPanel({
  title = "Details",
  activeTab = "details",
  sections = [],
  logsContent,
  actionsContent,
  onTabChange,
  onClose,
  onEdit,
  onDelete,
  className,
  "data-testid": testId = "right-panel",
}: RightPanelProps) {
  return (
    <aside
      className={cn(
        "flex w-60 flex-col rounded-2xl border border-glass-stroke bg-glass-fill shadow-glass-sm backdrop-blur-glass-sm",
        className,
      )}
      data-testid={testId}
    >
      {/* Header */}
      <div className="flex h-11 items-center gap-2 border-b border-glass-stroke-subtle px-3.5">
        <Icons.Info className="h-3.5 w-3.5 shrink-0 text-accent" aria-hidden="true" />
        <span className="text-xs font-semibold text-primary">{title}</span>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onClose}
          className="flex items-center justify-center rounded-md p-0.5 text-muted transition-colors hover:text-primary"
          data-testid="right-panel-close"
          aria-label="Close panel"
        >
          <Icons.X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-glass-stroke-subtle px-2 py-1" role="tablist" aria-label="Panel tabs">
        <RightPanelTabButton
          label="Details"
          isActive={activeTab === "details"}
          onClick={() => onTabChange?.("details")}
          testId="right-panel-tab-details"
        />
        <RightPanelTabButton
          label="Logs"
          isActive={activeTab === "logs"}
          onClick={() => onTabChange?.("logs")}
          testId="right-panel-tab-logs"
        />
        <RightPanelTabButton
          label="Actions"
          isActive={activeTab === "actions"}
          onClick={() => onTabChange?.("actions")}
          testId="right-panel-tab-actions"
        />
      </div>

      {/* Scrollable content */}
      <div className="scrollbar-none flex-1 overflow-y-auto">
        {activeTab === "details" && (
          <div className="flex flex-col" data-testid="right-panel-details">
            {sections.map((section, index) => (
              <RightPanelSection
                key={index}
                section={section}
                testId={`right-panel-section-${index}`}
              />
            ))}
            {sections.length === 0 && (
              <p className="px-4 py-6 text-center text-[10px] text-muted">
                No details available
              </p>
            )}
            <div className="flex items-center gap-2 px-3.5 py-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={onEdit}
                data-testid="right-panel-edit"
              >
                <Icons.Edit className="h-3 w-3" />
                Edit
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={onDelete}
                data-testid="right-panel-delete"
              >
                <Icons.Trash2 className="h-3 w-3" />
                Delete
              </Button>
            </div>
          </div>
        )}

        {activeTab === "logs" && (
          <div className="p-4" data-testid="right-panel-logs">
            {logsContent ?? (
              <p className="text-center text-[10px] text-muted">No logs yet</p>
            )}
          </div>
        )}

        {activeTab === "actions" && (
          <div className="p-4" data-testid="right-panel-actions">
            {actionsContent ?? (
              <p className="text-center text-[10px] text-muted">No actions available</p>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}

export type { RightPanelProps, RightPanelTab, DetailSection, DetailRow };
